/*  Shared  */
import { useQuery } from '@tanstack/react-query';
import { Response } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';
import { TenantStat } from 'application/Shared/types/tenant';

/*  NPM  */
import axios from 'axios';
import { format } from 'date-fns';

interface GetGlobalAdminTenantStatParams {
    day: string;
    global: boolean;
    key: string;
    size: number;
}

export interface PartialTenantStatsMap {
    platformTenantId: string;
    key: string;
    value: number;
}

export interface TenantStatNumbers {
    totalClients: number;
    totalPartnerDevices: number;
    totalPartnerUsers: number;
}

export interface TenantStatsMapInterface {
    [key: string]: TenantStatNumbers;
}

export const useFetchGlobalAdminTenantStat = ({
    enabled = true
}: {
    enabled: boolean;
}) => {
    // cron job runs on EST
    const date = format(new Date(), 'yyyy-MM-dd');

    const { data, isLoading, refetch } = useQuery<TenantStat[]>({
        enabled,
        queryFn: async () => {
            try {
                const params: GetGlobalAdminTenantStatParams = {
                    day: date,
                    global: true,
                    key: '[totalClients,totalPartnerUsers,totalPartnerDevices]',
                    size: -1
                };
                const res = await axios.get('/tenantStat', { params });

                // Use Response helper if available; otherwise, just return and empty array
                if (Response.hasData(res)) {
                    return Response.getData(res);
                }
                return [];
            } catch (error) {
                debug.e('Failed to fetch tenant statistics.', error);
                return [];
            }
        },
        queryKey: [
            'tenantStat',
            date,
            '[totalClients,totalPartnerUsers,totalPartnerDevices]'
        ]
    });

    const tenantStatsMap = data?.reduce<TenantStatsMapInterface>(
        (
            acc: TenantStatsMapInterface,
            { platformTenantId, key, value }: PartialTenantStatsMap
        ) => {
            const tenantStats = acc[platformTenantId] || {
                totalClients: 0,
                totalPartnerDevices: 0,
                totalPartnerUsers: 0
            };
            // @ts-expect-error typing is hard
            tenantStats[key] = value;
            return { ...acc, [platformTenantId]: tenantStats };
        },
        {}
    );

    return {
        loading: isLoading,
        refetchData: refetch,
        tenantStats: tenantStatsMap
    };
};
